import {
  type Analytics,
  logEvent as firebaseLogEvent,
  setUserProperties,
  setAnalyticsCollectionEnabled,
} from 'firebase/analytics';
import { initAnalytics } from '@/config/firebase';

/**
 * Centralized Firebase Analytics service.
 *
 * Mirrors Flutter FirebaseAnalyticService exactly — preserves event names,
 * parameter names, and trigger conditions from the Flutter implementation.
 *
 * Flutter source: domain/core/services/firebase_analytics_service.dart
 *
 * Usage:
 *   import { analyticsService } from '@/services/analytics/analyticsService';
 *   analyticsService.logLogin({ method: 'email' });
 */

// One shared init — events fired while Firebase Analytics is still starting wait for it
let analyticsInstance: Promise<Analytics | null> | null = null;

function getAnalyticsInstance(): Promise<Analytics | null> {
  analyticsInstance ??= initAnalytics()
    .then((a) => a ?? null)
    .catch(() => null);
  return analyticsInstance;
}

function getTenantId(): string {
  try {
    return localStorage.getItem('workspaceId') ?? '';
  } catch {
    return '';
  }
}

// ─── Init ────────────────────────────────────────────────────────────────────

async function init(): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;

  setAnalyticsCollectionEnabled(a, true);

  setUserProperties(a, { tenant_id: getTenantId() });

  // Flutter logAppOpen(): analytics.logAppOpen() (standard event, no parameters), then the
  // custom logEvent('app', 'app_open')
  firebaseLogEvent(a, 'app_open');
  firebaseLogEvent(a, 'app', {
    action: 'app_open',
    tenant_id: getTenantId(),
  });
}

// ─── Set tenant user property ────────────────────────────────────────────────

async function setTenantUserProperty(tenantId?: string): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  setUserProperties(a, { tenant_id: tenantId ?? getTenantId() });
}

// ─── Authentication Events ───────────────────────────────────────────────────

async function logLogin(params: { method: string }): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  firebaseLogEvent(a, 'login', {
    login_method: params.method,
    tenant_id: getTenantId(),
  });
}

async function logSignUp(params: { method: string }): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  firebaseLogEvent(a, 'sign_up', {
    sign_up_method: params.method,
    tenant_id: getTenantId(),
  });
  firebaseLogEvent(a, 'authentication', {
    action: 'signup',
    tenant_id: getTenantId(),
    details: params.method,
  });
}

// ─── Job Events ──────────────────────────────────────────────────────────────

async function logJobView(params: {
  jobId: string;
  jobTitle: string;
  location?: string;
}): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  firebaseLogEvent(a, 'job_view', {
    job_id: params.jobId,
    job_title: params.jobTitle,
    ...(params.location ? { location: params.location } : {}),
    tenant_id: getTenantId(),
  });
}

async function logJobSave(params: {
  jobId: string;
  jobTitle: string;
  location?: string;
}): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  const eventName = params.location ?? 'job_save';
  firebaseLogEvent(a, eventName, {
    job_id: params.jobId,
    job_title: params.jobTitle,
    tenant_id: getTenantId(),
  });
}

async function logJobUnsave(params: {
  jobId: string;
  jobTitle: string;
  location?: string;
}): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  const eventName = params.location ?? 'job_unsave';
  firebaseLogEvent(a, eventName, {
    job_id: params.jobId,
    job_title: params.jobTitle,
    tenant_id: getTenantId(),
  });
}

async function logJobShare(params: {
  jobId: string;
  jobTitle: string;
  shareMethod: string;
}): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  firebaseLogEvent(a, 'share', {
    content_type: 'job',
    item_id: params.jobId,
    method: params.shareMethod,
  });
  firebaseLogEvent(a, 'job_share', {
    job_id: params.jobId,
    job_title: params.jobTitle,
    share_method: params.shareMethod,
    tenant_id: getTenantId(),
  });
}

async function logJobApply(params: {
  jobId: string;
  jobTitle: string;
  location?: string;
}): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  firebaseLogEvent(a, 'job_apply', {
    job_id: params.jobId,
    job_title: params.jobTitle,
    ...(params.location ? { location: params.location } : {}),
    tenant_id: getTenantId(),
  });
}

// ─── Search Events ───────────────────────────────────────────────────────────

/**
 * Mirrors Flutter logJobSearch filter sanitising: strings/numbers pass through,
 * lists contribute their first item (a filter value's `key`), other objects are stringified.
 */
function sanitizeSearchFilters(filters?: Record<string, unknown>): Record<string, string | number> {
  const sanitized: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(filters ?? {})) {
    if (typeof value === 'string' || typeof value === 'number') {
      sanitized[key] = value;
    } else if (Array.isArray(value)) {
      const first: unknown = value[0];
      if (first == null) continue;
      if (typeof first === 'string' || typeof first === 'number') {
        sanitized[key] = String(first);
      } else if (typeof first === 'object') {
        sanitized[key] = String((first as { key?: unknown }).key ?? '');
      }
    } else if (value != null) {
      sanitized[key] = String(value);
    }
  }
  return sanitized;
}

async function logJobSearch(params: {
  searchTerm: string;
  filters?: Record<string, unknown>;
}): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  firebaseLogEvent(a, 'search', {
    search_term: params.searchTerm,
    tenant_id: getTenantId(),
    ...sanitizeSearchFilters(params.filters),
  });
}

// ─── Profile Events ─────────────────────────────────────────────────────────

async function logProfileUpdate(params: {
  userId: string;
  fieldUpdated: string;
}): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  firebaseLogEvent(a, 'profile_update', {
    user_id: params.userId,
    field_updated: params.fieldUpdated,
    tenant_id: getTenantId(),
  });
}

// ─── Language Events ─────────────────────────────────────────────────────────

async function logLanguageChange(params: {
  fromLanguage: string;
  toLanguage: string;
}): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  firebaseLogEvent(a, 'language_change', {
    from_language: params.fromLanguage,
    to_language: params.toLanguage,
    tenant_id: getTenantId(),
  });
}

// ─── Screen Tracking ─────────────────────────────────────────────────────────

/**
 * Flutter logs this only from NavigationService.navigateTo(route, analyticsClass), so call it
 * right before the matching navigation: screenName = the Flutter route, screenClass = the
 * caller's analyticsClass. FlutterFire web sends screen_view with `screen_class` / `screen_name`
 * (left out when null) plus the custom tenant_id.
 */
async function logScreenView(params: {
  screenName: string;
  screenClass?: string;
}): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  // Typed as a plain string: the SDK's 'screen_view' overload insists on the JS SDK's own
  // firebase_screen / firebase_screen_class names, which Flutter doesn't send
  const screenViewEvent: string = 'screen_view';
  firebaseLogEvent(a, screenViewEvent, {
    ...(params.screenClass != null ? { screen_class: params.screenClass } : {}),
    screen_name: params.screenName,
    tenant_id: getTenantId(),
  });
}

// ─── Generic Event ───────────────────────────────────────────────────────────

async function logEvent(
  page: string,
  action: string,
  other?: string,
): Promise<void> {
  const a = await getAnalyticsInstance();
  if (!a) return;
  firebaseLogEvent(a, page, {
    action,
    tenant_id: getTenantId(),
    ...(other ? { details: other } : {}),
  });
}

// ─── Public API ──────────────────────────────────────────────────────────────

export const analyticsService = {
  init,
  setTenantUserProperty,
  logLogin,
  logSignUp,
  logJobView,
  logJobSave,
  logJobUnsave,
  logJobShare,
  logJobApply,
  logJobSearch,
  logProfileUpdate,
  logLanguageChange,
  logScreenView,
  logEvent,
};
